<script setup>
import { Handle, Position } from '@vue-flow/core';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import en from './en.json';
import ru from './ru.json';
import { startMessages } from './startSchedule';

defineProps({
  kind: { type: String, required: true },
  icon: { type: String, required: true },
  label: { type: String, default: '' },
  summary: { type: String, default: '' },
  source: { type: String, default: '' },
  outputs: { type: Array, default: () => [] },
  issue: { type: String, default: '' },
  active: { type: Boolean, default: false },
  visited: { type: Boolean, default: false },
  dimmed: { type: Boolean, default: false },
});
defineEmits(['add', 'configure']);
const { t } = useI18n({
  useScope: 'local',
  messages: {
    en: { ...en, ...startMessages.en },
    ru: { ...ru, ...startMessages.ru },
  },
});
</script>

<template>
  <div
    class="relative w-64 rounded-xl border bg-n-solid-1 shadow-sm transition-[box-shadow,opacity] duration-150 motion-reduce:transition-none hover:shadow-md"
    :class="[
      active
        ? 'border-n-brand ring-2 ring-n-brand/15'
        : visited
          ? 'border-n-teal-9'
          : 'border-n-weak',
      dimmed ? 'opacity-40' : 'opacity-100',
    ]"
  >
    <Handle
      v-if="kind !== 'trigger'"
      type="target"
      :position="Position.Left"
      class="!size-4 !border-2 !bg-n-solid-1 !border-n-slate-8"
      :title="t('editor.input')"
    />
    <div class="px-4 pt-4 pb-3">
      <div class="flex items-center gap-2.5 mb-2">
        <div
          class="flex items-center justify-center size-8 shrink-0 rounded-lg bg-n-alpha-1 text-n-brand"
          aria-hidden="true"
        >
          <span :class="icon" class="size-4" />
        </div>
        <span class="text-xs font-semibold text-n-slate-11">{{ t(kind) }}</span>
        <span
          v-if="visited"
          class="i-lucide-circle-check ms-auto size-4 text-n-teal-11"
          :title="t('editor.inPreview')"
        />
        <span
          v-else-if="issue"
          class="i-lucide-circle-alert ms-auto size-4 text-n-amber-11"
          :title="issue"
        />
      </div>
      <button
        type="button"
        class="nodrag nopan block w-full text-start text-sm font-medium leading-5 text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand rounded"
        @click.stop="$emit('configure')"
      >
        <span class="line-clamp-2">{{ label || summary }}</span>
      </button>
      <p
        v-if="label && summary !== label"
        class="m-0 mt-1 text-xs text-n-slate-11 leading-5 line-clamp-2"
      >
        {{ summary }}
      </p>
      <p
        v-if="source"
        class="m-0 mt-2 text-xs text-n-slate-11 leading-4 line-clamp-2"
      >
        {{ source }}
      </p>
    </div>
    <div v-if="outputs.length" class="border-t border-n-weak py-1">
      <div
        v-for="output in outputs"
        :key="output.id"
        class="group relative flex items-center gap-2 min-h-8 px-4 text-xs text-n-slate-11"
      >
        <span class="truncate flex-1" :title="output.label">{{
          output.label
        }}</span>
        <span
          v-if="output.terminal"
          class="flex items-center gap-1 text-n-slate-10"
          :title="t('editor.endBranch')"
          ><span class="i-lucide-circle-stop size-3" aria-hidden="true" />{{
            t('editor.end')
          }}</span
        >
        <Button
          icon="i-lucide-plus"
          variant="ghost"
          color="slate"
          class="nodrag nopan !size-7 !min-w-0 shrink-0 !rounded-full"
          :aria-label="t('editor.addAfterOutput', { output: output.label })"
          :title="t('editor.addAfterOutput', { output: output.label })"
          @click.stop="$emit('add', output.id)"
        />
        <Handle
          :id="output.id"
          type="source"
          :position="Position.Right"
          class="!-right-2 !top-1/2 !size-4 !border-2 !bg-n-solid-1"
          :class="output.connected ? '!border-n-brand' : '!border-n-slate-8'"
          :title="output.label"
        />
      </div>
    </div>
  </div>
</template>
